import { useState } from "react";
import { Zap, ChevronDown } from "lucide-react";
import { Kbd } from "~/components/Kbd";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import { ZENDESK_AI_SKILLS_URL } from "~/components/assistant/constants";
import type { PartialSkill } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface SkillSelectorProps {
  systemSkills: PartialSkill[];
  userSkills: PartialSkill[];
  datasetSkills?: PartialSkill[];
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skillId: string) => void;
  onDeleteSkill?: (skillId: string) => void;
}

export function SkillSelector({
  systemSkills,
  userSkills,
  datasetSkills,
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
}: SkillSelectorProps) {
  const [open, setOpen] = useState(false);

  const handleSelectSkill = (skillName: string) => {
    onSelectSkill(skillName);
    setOpen(false);
  };

  return (
    <>
      <Tooltip delayDuration={700}>
        <Popover open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <PopoverButton
                variant="ghost"
                className="has-[>svg]:px-2"
                aria-label="Select AI Skill"
              >
                <Zap className="size-4 text-gray-600" />
                <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-700 text-[13px] text-left text-nowrap">
                  <p className="block leading-[16px] whitespace-pre">Skills</p>
                </div>
                <ChevronDown className="size-4 text-gray-400" />
              </PopoverButton>
            </PopoverTrigger>
          </TooltipTrigger>
          <PopoverContent
            className="w-84 p-0"
            align="start"
            side="bottom"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <SkillsPopup
              systemSkills={systemSkills}
              userSkills={userSkills}
              datasetSkills={datasetSkills}
              onSelectSkill={handleSelectSkill}
              onAddSkill={onAddSkill}
              onEditSkill={onEditSkill}
              onDeleteSkill={onDeleteSkill}
              onLearnMore={() =>
                window.open(ZENDESK_AI_SKILLS_URL, "_blank", "noopener,noreferrer")
              }
              onClose={() => setOpen(false)}
            />
          </PopoverContent>
        </Popover>
        <TooltipContent className="max-w-[150px]">
          <div className="flex items-center gap-2">
            <p className="text-xs">Use AI skills</p>
            <Kbd>/</Kbd>
          </div>
        </TooltipContent>
      </Tooltip>
    </>
  );
}
